import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Send,
  Loader2,
  Image,
  Video,
  X,
  ArrowLeft,
  Bot,
  User,
  Upload,
  Paperclip,
  Mic,
  MicOff,
  RefreshCw,
  Download,
  AlertCircle
} from 'lucide-react';
import './Chatbot.css';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:8080/api');

const AI_MODELS = {
  gpt4o: { 
    name: 'AI Assistant 1', 
    color: '#10a37f',
    avatar: '🧠',
    greeting: "Hello! I'm your AI medical assistant. I'll help analyze your symptoms and medical concerns. Please describe what you're experiencing, and feel free to upload any relevant medical images or reports."
  },
  gemini: { 
    name: 'AI Assistant 2', 
    color: '#4285f4',
    avatar: '💎',
    greeting: "Hi there! I'm here to help with your medical questions. Tell me about your symptoms, and if you have any X-rays, scans, or medical images, please share them for a more accurate assessment."
  },
  claude: { 
    name: 'AI Assistant 3', 
    color: '#d97706',
    avatar: '🔬',
    greeting: "Greetings! I'm specialized in detailed medical analysis. Please share your symptoms, medical history, and any concerns. If you have medical images or test results, uploading them will help me provide a more thorough assessment."
  }
};

function Chatbot({ modelId, onClose }) {
  const { currentUser, userProfile, isSuperAdmin } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  
  const model = AI_MODELS[modelId];

  // Initialize with greeting
  useEffect(() => {
    setMessages([{
      id: 1,
      role: 'assistant',
      content: model.greeting,
      timestamp: new Date()
    }]);
  }, [model.greeting]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map(file => ({
      file,
      id: Date.now() + Math.random(),
      name: file.name,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // Transcribe and add to input
        try {
          const formData = new FormData();
          formData.append('audio', blob, 'recording.webm');
          const response = await fetch(`${API_BASE_URL}/transcribe`, {
            method: 'POST',
            body: formData
          });
          const data = await response.json();
          if (data.transcription) {
            setInput(prev => prev + ' ' + data.transcription);
          }
        } catch (err) {
          console.error('Transcription failed:', err);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Could not access microphone:', err);
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const sendMessage = async () => {
    if ((!input.trim() && attachments.length === 0) || loading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input.trim(),
      attachments: attachments.map(a => ({
        name: a.name,
        type: a.type,
        preview: a.preview
      })),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('modelId', modelId);
      formData.append('message', input.trim());
      
      // Send conversation history for context
      const history = messages.map(m => ({
        role: m.role,
        content: m.content
      }));
      formData.append('history', JSON.stringify(history));
      
      // Add attachments
      attachments.forEach(a => {
        formData.append('files', a.file);
      });

      // Add user info
      if (currentUser) {
        formData.append('userId', currentUser.uid);
      }

      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'X-User-Id': currentUser?.uid || '',
          'X-User-Phone': currentUser?.phoneNumber || ''
        },
        body: formData
      });

      const data = await response.json();
      
      // Check for insufficient credits
      if (response.status === 402) {
        const errorMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: `⚠️ **Insufficient Credits**\n\nYou have ${data.creditsRemaining || 0} credits remaining. Each chat message costs 1 credit.\n\nPlease upgrade your plan to continue chatting.`,
          isError: true,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
        return;
      }

      if (data.success) {
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: data.response,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error(data.error || 'Failed to get response');
      }
    } catch (err) {
      console.error('Chat error:', err);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I apologize, but I encountered an error processing your request. Please try again.",
        isError: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setAttachments([]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const exportChat = () => {
    const chatText = messages.map(m => {
      const role = m.role === 'assistant' ? model.name : 'You';
      return `[${role}]\n${m.content}\n`;
    }).join('\n---\n\n');
    
    const blob = new Blob([chatText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-${model.name}-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
  };

  const resetChat = () => {
    setMessages([{
      id: 1,
      role: 'assistant',
      content: model.greeting,
      timestamp: new Date()
    }]);
    setAttachments([]);
    setInput('');
  };

  const renderMessage = (message) => {
    const isUser = message.role === 'user';
    
    return (
      <div key={message.id} className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
        <div className="message-avatar" style={{ background: isUser ? '#6366f1' : model.color }}>
          {isUser ? <User size={18} /> : model.avatar}
        </div>
        <div className="message-content">
          <div className="message-header">
            <span className="message-sender">{isUser ? 'You' : model.name}</span>
            <span className="message-time">
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          
          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="message-attachments">
              {message.attachments.map((att, idx) => (
                <div key={idx} className="attachment-preview">
                  {att.preview ? (
                    <img src={att.preview} alt={att.name} />
                  ) : (
                    <div className="attachment-file">
                      <Paperclip size={16} />
                      <span>{att.name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Message text */}
          <div className={`message-text ${message.isError ? 'error' : ''}`}>
            {message.content.split('\n').map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="chatbot-container">
      <div className="chatbot-header" style={{ borderColor: model.color }}>
        <button className="back-btn" onClick={onClose}>
          <ArrowLeft size={20} />
        </button>
        <div className="chatbot-title">
          <span className="model-avatar" style={{ background: model.color }}>{model.avatar}</span>
          <div>
            <h2>{model.name}</h2>
            <span className="model-subtitle">AI Medical Assistant</span>
          </div>
        </div>
        <div className="chatbot-actions">
          <button className="action-btn" onClick={resetChat} title="New Chat">
            <RefreshCw size={18} />
          </button>
          <button className="action-btn" onClick={exportChat} title="Export Chat">
            <Download size={18} />
          </button>
        </div>
      </div>

      <div className="chatbot-disclaimer">
        <AlertCircle size={14} />
        <span>AI suggestions require verification by qualified medical professionals.</span>
      </div>

      <div className="chatbot-messages">
        {messages.map(renderMessage)}
        
        {loading && (
          <div className="chat-message assistant">
            <div className="message-avatar" style={{ background: model.color }}>
              {model.avatar}
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className="attachments-bar">
          {attachments.map(att => (
            <div key={att.id} className="attachment-chip">
              {att.preview ? (
                <img src={att.preview} alt={att.name} />
              ) : (
                <Paperclip size={14} />
              )}
              <span>{att.name}</span>
              <button onClick={() => removeAttachment(att.id)}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="chatbot-input-area">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          multiple
          accept="image/*,video/*,.pdf"
          style={{ display: 'none' }}
        />
        
        <button 
          className="input-action-btn"
          onClick={() => fileInputRef.current?.click()}
          title="Attach files"
        >
          <Paperclip size={20} />
        </button>
        
        <button 
          className="input-action-btn"
          onClick={() => fileInputRef.current?.click()}
          title="Upload image"
        >
          <Image size={20} />
        </button>

        <button 
          className={`input-action-btn ${isRecording ? 'recording' : ''}`}
          onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
          title={isRecording ? 'Stop recording' : 'Voice input'}
        >
          {isRecording ? (
            <>
              <MicOff size={20} />
              <span className="recording-time">{formatTime(recordingTime)}</span>
            </>
          ) : (
            <Mic size={20} />
          )}
        </button>
        
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Describe your symptoms..."
          rows={1}
          disabled={loading}
        />
        
        <button 
          className="send-btn"
          onClick={sendMessage}
          disabled={loading || (!input.trim() && attachments.length === 0)}
          style={{ background: model.color }}
        >
          {loading ? <Loader2 size={20} className="spin" /> : <Send size={20} />}
        </button>
      </div>
    </div>
  );
}

export default Chatbot;
